import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  TablePagination,
  Stack,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTranslation } from 'react-i18next';
import { auditService, Finding, FindingSeverity } from '@services/auditService';
import AdvisoryNotesPanel from '@components/findings/AdvisoryNotesPanel';
import { RuleTitle } from '@components/findings/RuleTitle';
import { toDisplayError } from '../../utils/apiError';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';

interface FindingsTabProps {
  orgId: string;
  auditId: string;
}

type SourceFilter = 'all' | 'auto' | 'manual';

const SEVERITY_COLOR: Record<FindingSeverity, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  major: 'warning',
  minor: 'info',
  info: 'default',
};

const SOURCE_COLOR: Record<'auto' | 'manual', 'primary' | 'secondary'> = {
  auto: 'primary',
  manual: 'secondary',
};

export default function FindingsTab({ orgId, auditId }: FindingsTabProps) {
  const { t } = useTranslation();

  const [findings, setFindings] = useState<Finding[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [severityFilter, setSeverityFilter] = useState<FindingSeverity | ''>('');
  const [scFilter, setScFilter] = useState('');

  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchFindings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await auditService.listFindings(orgId, auditId, {
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        severity: severityFilter || undefined,
        wcag_sc_id: scFilter || undefined,
        limit,
        offset,
      });
      setFindings(res.data);
      setTotal(res.pagination.total);
    } catch (err) {
      setError(toDisplayError(err, t('findings.errors.loadFailed', 'Failed to load findings')));
    } finally {
      setLoading(false);
    }
  }, [orgId, auditId, sourceFilter, severityFilter, scFilter, limit, offset, t]);

  useEffect(() => {
    void fetchFindings();
  }, [fetchFindings]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('findings.title', 'Findings')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'findings.auditSubtitle',
          'Unified view of automated scan findings and manual reviewer verdicts for this audit.'
        )}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="findings-source-filter-label">
            {t('findings.filters.source', 'Source')}
          </InputLabel>
          <Select
            labelId="findings-source-filter-label"
            label={t('findings.filters.source', 'Source')}
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value as SourceFilter);
              setOffset(0);
            }}
          >
            <MenuItem value="all">{t('findings.filters.anySource', 'All sources')}</MenuItem>
            <MenuItem value="auto">{t('findings.source.auto', 'Automated')}</MenuItem>
            <MenuItem value="manual">{t('findings.source.manual', 'Manual')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="findings-severity-filter-label">
            {t('findings.filters.severity', 'Severity')}
          </InputLabel>
          <Select
            // REV-580: without this MUI renders a zero-width space for an
            // empty value, so the active default reads as 'no filter chosen'.
            displayEmpty
            labelId="findings-severity-filter-label"
            label={t('findings.filters.severity', 'Severity')}
            value={severityFilter}
            onChange={(e) => {
              setSeverityFilter(e.target.value as FindingSeverity | '');
              setOffset(0);
            }}
          >
            <MenuItem value="">{t('findings.filters.anySeverity', 'Any severity')}</MenuItem>
            <MenuItem value="critical">{t('findings.severity.critical', 'Critical')}</MenuItem>
            <MenuItem value="major">{t('findings.severity.major', 'Major')}</MenuItem>
            <MenuItem value="minor">{t('findings.severity.minor', 'Minor')}</MenuItem>
            <MenuItem value="info">{t('findings.severity.info', 'Info')}</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label={t('findings.filters.wcagSc', 'WCAG SC id (e.g. 1.1.1)')}
          value={scFilter}
          onChange={(e) => {
            setScFilter(e.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 180 }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : findings.length === 0 ? (
        <Alert severity="info">
          {t('findings.empty', 'No findings matched the current filters.')}
        </Alert>
      ) : (
        <>
          <ScrollableTableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>{t('findings.table.rule', 'Rule')}</TableCell>
                  <TableCell>{t('findings.table.source', 'Source')}</TableCell>
                  <TableCell>{t('findings.table.severity', 'Severity')}</TableCell>
                  <TableCell>{t('findings.table.wcag', 'WCAG')}</TableCell>
                  <TableCell>{t('findings.table.location', 'Location / Note')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {findings.map((f) => {
                  const isExpandable = f.source === 'auto';
                  const isExpanded = expandedId === f.id;
                  return (
                    <Fragment key={f.id}>
                      <TableRow hover>
                        <TableCell sx={{ width: 40 }}>
                          {isExpandable ? (
                            <Tooltip
                              title={
                                isExpanded
                                  ? t('findings.collapse', 'Hide advisory notes')
                                  : t('findings.expand', 'Show advisory notes')
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() => setExpandedId(isExpanded ? null : f.id)}
                                aria-label={
                                  isExpanded
                                    ? t('findings.collapse', 'Hide advisory notes')
                                    : t('findings.expand', 'Show advisory notes')
                                }
                                aria-expanded={isExpanded}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            <RuleTitle text={f.rule_name || f.rule_id || '—'} />
                          </Typography>
                          {f.rule_id && f.rule_name && f.rule_id !== f.rule_name && (
                            <Typography variant="caption" color="text.secondary">
                              {f.rule_id}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={f.source}
                            color={SOURCE_COLOR[f.source]}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={f.severity}
                            color={SEVERITY_COLOR[f.severity]}
                          />
                          {f.is_critical_path && (
                            <Tooltip title={t('findings.criticalPath', 'Critical path')}>
                              <Chip
                                size="small"
                                label="CP"
                                color="warning"
                                sx={{ ml: 0.5 }}
                                variant="filled"
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {f.wcag_criteria.length > 0
                            ? f.wcag_criteria.join(', ')
                            : t('findings.wcagNone', '—')}
                        </TableCell>
                        <TableCell>
                          {f.source === 'auto' ? (
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                            >
                              {f.element_selector || t('findings.noSelector', 'No selector')}
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              {f.verdict_note || t('findings.noNote', '(no note)')}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpandable && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <AdvisoryNotesPanel orgId={orgId} findingId={f.raw_id} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
          <TablePagination
            component="div"
            count={total}
            page={Math.floor(offset / limit)}
            onPageChange={(_, newPage) => setOffset(newPage * limit)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setOffset(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Box>
  );
}
